import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _has from 'lodash/has';
import PropTypes from 'prop-types';
// Models
import mDevice from 'models/device';
import mDeviceSlave from 'models/deviceSlave';
// Custom
import { getInstallationDevice, getInstallationDeviceSlave, updateInstallationDeviceSlave } from 'actions';
import { currentDeviceShape, currentDeviceSlaveShape, errorShape } from 'constants/PropTypesShapes';
import { buildFormErrors, installationDeviceSlaveUpdateConstraints } from 'utils/Forms';
import { configurationPath, navigateToConfiguration } from 'installations/navigation';
import withStateReinit from 'hocs/WithStateReinit';
import withFormManagement from 'hocs/WithFormManagement';
import FormTemplate from 'components/templates/FormTemplate';
import ConfigureDeviceSlave from 'installations/assets/configuration/ConfigureDeviceSlave';
import withResources from 'installations/assets/hocs/WithResources';

class InstallationDeviceSlavesShow extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
  }

  componentDidMount() {
    const { params, handleGetDevice, handleGetSlave } = this.props;
    handleGetDevice(params.id, params.idDevice);
    handleGetSlave(params.id, params.idDevice, params.idSlave);
  }

  handleValidate(e) {
    e.preventDefault();

    const {
      fields: {
        name, triggers, relays, inputs,
      },
      params, currentSlave, onErrorsChange, handleUpdateSlave,
    } = this.props;

    // Note: validate name only if the slave is not a motherboard
    const nameToCheck = mDeviceSlave.isMotherboard(currentSlave.data) ? 'motherboard' : name;
    const errors = buildFormErrors({ name: nameToCheck }, installationDeviceSlaveUpdateConstraints);

    // Apply errors
    onErrorsChange(errors);

    // No error => validate form
    if (!errors) {
      handleUpdateSlave(params.id, params.idDevice, params.idSlave, mDevice.serializeSlave({
        name, triggers, relays, inputs,
      }));
    }
  }

  handleCancel() {
    const { params } = this.props;
    navigateToConfiguration(params.id);
  }

  render() {
    const {
      fields: {
        name, triggers, relays, inputs,
      }, errors,
      params, currentDevice, currentSlave, onFieldsChange,
    } = this.props;

    const loading = currentDevice.loading || currentSlave.loading;

    const device = currentDevice.data;
    const slave = currentSlave.data;
    const virtualTriggerTypes = slave ? slave.virtual_trigger_types : [];

    // Build additional errors
    const additionalError = [];
    if (_has(errors, 'triggers.triggerEquipment.name')) {
      additionalError.push(<LocalizedMessage id="error.triggerEquipmentNameCantBeBlank" />);
    }
    if (_has(errors, 'triggers.triggerEquipment.ip')) {
      additionalError.push(<LocalizedMessage id="error.triggerEquipmentIpIsInvalid" />);
    }

    // Check if slave is motherboard
    const isMotherboard = mDeviceSlave.isMotherboard(slave);

    return (
      <FormTemplate
        loading={loading}
        title={slave ? <LocalizedMessage id="title.configureDeviceSlave" values={{ isMotherboard, desc: mDeviceSlave.installationDesc(slave) }} /> : ''}
        subtitle={device && mDevice.installationDesc(device)}
        errors={errors}
        additionalError={additionalError}
        backPath={configurationPath(params.id)}
        onValidate={this.handleValidate}
        onCancel={this.handleCancel}
      >
        <ConfigureDeviceSlave
          isMotherboard={isMotherboard}
          name={name}
          triggers={triggers}
          relays={relays}
          inputs={inputs}
          virtualTriggerTypes={virtualTriggerTypes}
          errors={errors}
          onChange={(value) => onFieldsChange({
            name: value.name, triggers: value.triggers, relays: value.relays, inputs: value.inputs,
          })}
        />
      </FormTemplate>
    );
  }
}

InstallationDeviceSlavesShow.propTypes = {
  params: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    idDevice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    idSlave: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  currentDevice: currentDeviceShape.isRequired,
  currentSlave: currentDeviceSlaveShape.isRequired,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  handleGetDevice: PropTypes.func.isRequired,
  handleGetSlave: PropTypes.func.isRequired,
  handleUpdateSlave: PropTypes.func.isRequired,
};

InstallationDeviceSlavesShow.defaultProps = {
  operationError: null,
  errors: null,
};

const mapStateToProps = (state) => ({
  currentDevice: state.installationDevice.current,
  currentSlave: state.installationDeviceSlave.current,
  operationError: state.installationDeviceSlave.update.error,
});

const mapDispatchToProps = (dispatch) => ({
  handleGetDevice: (id, idDevice) => {
    dispatch(getInstallationDevice(id, idDevice));
  },
  handleGetSlave: (id, idDevice, idSlave) => {
    dispatch(getInstallationDeviceSlave(id, idDevice, idSlave));
  },
  handleUpdateSlave: (id, idDevice, idSlave, data) => {
    dispatch(updateInstallationDeviceSlave(id, idDevice, idSlave, data));
  },
});

const mapFieldsToProps = {
  name: {
    resourceKey: 'name',
    defaultValue: '',
  },
  triggers: {
    resourceKey: 'triggers',
    defaultValue: [],
  },
  relays: {
    resourceKey: 'relays',
    defaultValue: [],
  },
  inputs: {
    resourceKey: 'inputs',
    defaultValue: [],
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: ownProps.currentSlave.data,
  operationError: ownProps.operationError,
});

module.exports = compose(
  withResources(
    (ownProps) => ({ id: ownProps.params.id }),
    ['equipments', 'doors', 'sources'],
  ),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources),
  withStateReinit(['updateInstallationDeviceSlave']),
)(InstallationDeviceSlavesShow);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/routes/show/routes/configuration/routes/devices/routes/show/routes/slaves/routes/show/routes/index/components/InstallationDeviceSlavesShow.js